import {
  defineComponent,
  getCurrentInstance,
  h,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  watch,
} from 'vue'
import { debounce } from 'lodash-es'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import { useFormSize } from '@zoryn/components/form'
import { SIZE_INJECTION_KEY, useLocale } from '@zoryn/hooks'
import { GRID_HTTP_INJECTION_KEY } from './constants/grid-http'
import { gridProps } from './props'
import { createInternalData, createReactData } from './store'
import { attachGridMethods } from './methods'
import { createLoadColumns } from './load-columns'
import {
  resolveParentCurrentRow,
  resolveParentRowId,
} from './api/api-convention'
import GridLayout from './components/grid-layout.vue'
import { resolveGridOptions } from './utils/resolve-grid-options'
import { ensureGridRequest } from './utils/resolve-grid-request'
import type {
  DataGrid,
  DataGridBase,
  GridColumnProps,
  GridOptions,
} from './types'

export default defineComponent({
  name: 'ZGrid',
  props: gridProps,
  setup(props, { expose }) {
    useLocale()
    const lovConfig = useGlobalConfig('lov')
    const gridSize = useFormSize()
    provide(SIZE_INJECTION_KEY, { size: gridSize })
    const options = resolveGridOptions(props.options) as GridOptions
    const reactData = createReactData(options)
    if (!Array.isArray(reactData.filterGroups)) {
      reactData.filterGroups = []
    }
    const internalData = reactive(createInternalData())

    const instance = getCurrentInstance()
    const shell = {
      props,
      options,
      reactData,
      internalData,
      gridSize,
      router: instance?.appContext.config.globalProperties.$router as
        | DataGridBase['router']
        | undefined,
    } satisfies DataGridBase
    const $grid = shell as unknown as DataGrid

    $grid.options = options
    ensureGridRequest($grid, inject(GRID_HTTP_INJECTION_KEY, null))
    attachGridMethods($grid)

    const loadColumns = createLoadColumns($grid, () => lovConfig.value)
    $grid.loadColumns = (columns: GridColumnProps[]) => {
      void loadColumns(columns)
    }

    const debouncedAutoQuery = debounce(() => {
      if (!options.module) {
        return
      }
      void $grid.queryByExamplePage()
    }, 100)

    if (options.parent) {
      watch(
        () => resolveParentRowId(options.parent),
        (parentId, prevId) => {
          if (parentId != null && parentId !== prevId) {
            debouncedAutoQuery()
          }
        },
        { flush: 'post' }
      )
    }

    watch(
      () => options.params,
      () => {
        debouncedAutoQuery()
      },
      { deep: true }
    )

    watch(
      () => props.options,
      (next) => {
        if (next && next !== options) {
          Object.assign(options, next)
        }
      }
    )

    watch(
      () => props.options?.columns,
      (columns) => {
        if (columns?.length) {
          void loadColumns(columns)
        }
      }
    )

    if (options.columns?.length) {
      void loadColumns(options.columns)
    }

    options.getCurrentRow = () => reactData.currentRow
    options.getAllCheckbox = () => $grid.getAllCheckbox?.() ?? []
    onMounted(() => {
      if (options.autoQuery === false) {
        return
      }
      if (!options.module) {
        return
      }
      if (!options.parent || resolveParentCurrentRow(options.parent) != null) {
        void $grid.queryByExamplePage()
      }
    })

    onBeforeUnmount(() => {
      debouncedAutoQuery.cancel()
      reactData.tableData = []
    })

    provide('$grid', $grid)

    expose({
      getCurrentRow: () => reactData.currentRow,
      getAllCheckbox: () => $grid.getAllCheckbox?.() ?? [],
      loadTableData: $grid.loadTableData,
      queryByExamplePage: $grid.queryByExamplePage,
      loadMore: $grid.loadMore,
    })

    return () => h(GridLayout)
  },
})
